import {useQuery} from '@tanstack/react-query';

import {useApiClient} from '@/hooks';
import {useUserDetails} from '@/lib/auth';
import {readLocalStorageJSON, writeLocalStorageJSON} from '@/utils';

import {firmSettingsApi} from '../api';

import type {ApiResponse, FirmProfileDTO} from '@sollapay/types';

export const firmSettingsKeys = {
  all: ['firm-settings'] as const,
  profile: () => [...firmSettingsKeys.all, 'profile'] as const
};

function profileStorageKey(firmId: string): string {
  return `sollapay:firm-settings:profile:${firmId}`;
}

export function persistFirmProfile(firmId: string | undefined, profile: FirmProfileDTO): void {
  if (firmId) writeLocalStorageJSON(profileStorageKey(firmId), profile);
}

export function useFirmProfileQuery() {
  const client = useApiClient();
  const {org} = useUserDetails();
  const firmId = org?.id;

  return useQuery({
    queryKey: firmSettingsKeys.profile(),
    queryFn: async () => {
      const response = await firmSettingsApi.fetchProfile(client);
      persistFirmProfile(firmId, response.data);
      return response;
    },
    select: response => response.data,
    initialData: firmId
      ? (): ApiResponse<FirmProfileDTO> | undefined => {
          const cached = readLocalStorageJSON<FirmProfileDTO>(profileStorageKey(firmId));
          return cached ? {data: cached} : undefined;
        }
      : undefined,
    initialDataUpdatedAt: 0,
    staleTime: 1000 * 60 * 60 * 4
  });
}
